import { Draggable } from "@hello-pangea/dnd";
import { ConfirmationModal } from "@shared/components/ConfirmationModal";
import EditMenu from "@shared/components/EditMenu";
import ModalBlank from "@shared/components/modals/ModalBlank";
import theme from "@shared/utils/themeColors";
import { parseISO } from "date-fns";
import { format } from "date-fns-tz";
import { enUS } from "date-fns/locale";
import React, { useState } from "react";
import { Task, TaskStatus } from "../types/task.types";
import { SubTaskForm } from "./SubTaskForm";
import { SubTaskItem } from "./SubTaskItem";
import { TaskForm } from "./TaskForm";

interface TaskItemProps {
  task: Task;
  isCompleted?: boolean;
  index: number;
  onToggleStatus: (taskId: string, currentStatus: TaskStatus) => Promise<void>;
  onUpdateTask: (
    taskId: string,
    data: { title: string; description?: string; dueDate?: Date }
  ) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onAddSubtask: (taskId: string, title: string) => Promise<void>;
  onToggleSubtask: (taskId: string, subtaskId: string) => Promise<void>;
  onUpdateSubtask: (
    taskId: string,
    subtaskId: string,
    title: string
  ) => Promise<void>;
  onDeleteSubtask: (taskId: string, subtaskId: string) => Promise<void>;
  onReorderSubtask: (taskId: string, subtaskIds: string[]) => Promise<void>;
}

export const TaskItem: React.FC<TaskItemProps> = ({
  task,
  isCompleted = false,
  index,
  onToggleStatus,
  onUpdateTask,
  onDeleteTask,
  onAddSubtask,
  onToggleSubtask,
  onUpdateSubtask,
  onDeleteSubtask,
  onReorderSubtask,
}) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddSubtaskModal, setShowAddSubtaskModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return null;

    try {
      let date;

      if (timestamp.toDate) {
        // Convert Firebase Timestamp to date
        date = timestamp.toDate();
      } else if (typeof timestamp === "string") {
        date = parseISO(timestamp);
      } else {
        date = timestamp;
      }

      // Get timezone offset in minutes and convert to milliseconds
      const timezoneOffset = date.getTimezoneOffset() * 60 * 1000;

      // Adjust date by adding the timezone offset
      const adjustedDate = new Date(date.getTime() + timezoneOffset);

      // Format date in English
      return format(adjustedDate, "MMM d", { locale: enUS });
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  const handleDeleteTask = async () => {
    setIsDeleting(true);
    try {
      await onDeleteTask(task.id);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const handleAddSubtask = async (title: string) => {
    await onAddSubtask(task.id, title);
    setShowAddSubtaskModal(false);
  };

  return (
    <>
      <Draggable draggableId={task.id} index={index}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`bg-white dark:bg-gray-800 shadow-sm rounded-xl p-4 `}
          >
            <div className="sm:flex sm:justify-between sm:items-start">
              {/* Left side */}
              <div
                className={`grow mt-0.5 mb-3 sm:mb-0 space-y-3 ${
                  isCompleted ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-center">
                  {/* Checkbox button */}
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={task.status === TaskStatus.COMPLETED}
                      onChange={() => onToggleStatus(task.id, task.status)}
                      className="form-checkbox w-5 h-5 rounded-full peer cursor-pointer"
                    />
                    <span
                      className={`font-medium ${theme.text.primary} peer-checked:line-through ml-2`}
                    >
                      {task.title}
                    </span>
                  </label>
                </div>
                {task.description && (
                  <p className={`text-sm ${theme.text.muted}`}>
                    {task.description}
                  </p>
                )}
                {/* Subtasks */}
                {task.subtasks && task.subtasks.length > 0 && (
                  <ul className="pl-12">
                    {task.subtasks.map((subtask, index) => (
                      <li key={subtask.id}>
                        <SubTaskItem
                          taskId={task.id}
                          subtask={subtask}
                          onToggle={onToggleSubtask}
                          onUpdate={onUpdateSubtask}
                          onDelete={onDeleteSubtask}
                          onReorder={onReorderSubtask}
                          isCompleted={subtask.completed}
                          totalSubtasks={task.subtasks?.length || 0}
                          position={index}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {/* Right side */}
              <div className="flex items-center justify-end space-x-3">
                {task.dueDate && (
                  <div
                    className={`flex items-center ${theme.text.current} ${
                      isCompleted ? "opacity-60" : ""
                    }`}
                  >
                    <svg
                      className="shrink-0 fill-current mr-1.5"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                    >
                      <path d="M5 4a1 1 0 0 0 0 2h6a1 1 0 1 0 0-2H5Z" />
                      <path d="M4 0a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4V4a4 4 0 0 0-4-4H4ZM2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Z" />
                    </svg>
                    <div className={`text-sm ${theme.text.current} w-[3rem]`}>
                      {formatDate(task.dueDate)}
                    </div>
                  </div>
                )}
                <EditMenu
                  align="right"
                  className="relative inline-flex opacity-100 z-100"
                >
                  <li>
                    <button
                      className="font-medium text-sm text-gray-600 hover:text-gray-700 flex py-1 px-3 items-center"
                      onClick={() => setShowAddSubtaskModal(true)}
                    >
                      <svg
                        className="w-4 h-4 fill-current shrink-0"
                        viewBox="0 0 16 16"
                      >
                        <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                      </svg>
                      <span className="ml-2">Add Subtask</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className="font-medium text-sm text-gray-600 hover:text-gray-700 flex py-1 px-3 items-center"
                      onClick={() => setShowEditModal(true)}
                    >
                      <svg
                        className="w-4 h-4 fill-current shrink-0"
                        viewBox="0 0 16 16"
                      >
                        <path d="M11.7.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM4.6 14H2v-2.6l6-6L9.4 4 11 2.4 13.6 5 12 6.6z" />
                      </svg>
                      <span className="ml-2">Edit Task</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={`font-medium text-sm ${theme.text.error} hover:text-red-600 flex py-1 px-3 items-center`}
                      onClick={() => setShowDeleteConfirmation(true)}
                    >
                      <svg
                        className={`w-4 h-4 fill-current ${theme.text.error} shrink-0`}
                        viewBox="0 0 16 16"
                      >
                        <path d="M5 7h2v6H5V7zm4 0h2v6H9V7zm3-6v2h4v2h-1v10c0 .6-.4 1-1 1H2c-.6 0-1-.4-1-1V5H0V3h4V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1zM6 2v1h4V2H6zm7 3H3v9h10V5z" />
                      </svg>
                      <span className="ml-2">Delete Task</span>
                    </button>
                  </li>
                </EditMenu>
              </div>
            </div>
          </div>
        )}
      </Draggable>
      {/* Edit Modal */}
      <ModalBlank
        id="edit-task-modal"
        modalOpen={showEditModal}
        setModalOpen={setShowEditModal}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
              Edit Task
            </h2>
            <button
              className="text-slate-400 hover:text-slate-500"
              onClick={() => setShowEditModal(false)}
            >
              <span className="sr-only">Close</span>
              <svg className="w-4 h-4 fill-current">
                <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.414L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
              </svg>
            </button>
          </div>
          <TaskForm
            task={task}
            onSubmit={async (data) => {
              await onUpdateTask(task.id, data);
              setShowEditModal(false);
            }}
            onCancel={() => setShowEditModal(false)}
          />
        </div>
      </ModalBlank>

      {/* Add Subtask Modal */}
      <ModalBlank
        id="add-subtask-modal"
        modalOpen={showAddSubtaskModal}
        setModalOpen={setShowAddSubtaskModal}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
              Add Subtask
            </h2>
            <button
              className="text-slate-400 hover:text-slate-500"
              onClick={() => setShowAddSubtaskModal(false)}
            >
              <span className="sr-only">Close</span>
              <svg className="w-4 h-4 fill-current">
                <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.414L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
              </svg>
            </button>
          </div>
          <SubTaskForm
            onSubmit={handleAddSubtask}
            onCancel={() => setShowAddSubtaskModal(false)}
          />
        </div>
      </ModalBlank>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteTask}
        isLoading={isDeleting}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};
